<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoadingPrograms || isLoadingLocations">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/4">
          <base-skeleton-loader class="tw-h-3 tw-w-full" />
        </div>
        <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        <div class="tw-flex-grow tw-grid tw-grid-cols-3 tw-gap-4">
          <base-skeleton-loader class="tw-col-span-1 tw-h-3" />
          <base-skeleton-loader class="tw-col-span-1 tw-h-3" />
          <base-skeleton-loader class="tw-col-span-1 tw-h-3" />
        </div>
      </div>
    </div>
    <div v-else class="tw-p-4">
      <div class="tw-mb-6">
        <base-advanced-select
          label="Locations"
          placeholder="Select one or more locations"
          :options="orderedLocations"
          v-model="selectedLocationIds"
          label-key="name"
          value-key="businessLocationId"
          multiple
          :required="true"
          class="tw-flex-grow"
        />
      </div>

      <div v-if="selectedLocationIds.length === 0" class="tw-px-4 sm:tw-px-6 tw-mt-4 tw-text-gray-600 tw-italic">
        Please select one or more locations above to copy program pricing.
      </div>
      <div v-else class="tw-px-4 sm:tw-px-6 tw-mt-4">
        <div class="tw-grid tw-grid-cols-1 tw-gap-y-6">
          <div v-for="row in orderedUiRows" :key="row.name" class="tw-flex tw-items-center tw-gap-x-4 tw-border-b tw-pb-4">
            <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
              <div class="tw-flex tw-items-center">
                <base-checkbox v-model="row.selected" class="tw-mr-2" />
                <div class="tw-flex-col tw-my-auto tw-font-normal">
                  {{ row.name }}
                </div>
              </div>
              <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
            </div>
            <div :class="['tw-flex-grow tw-grid tw-gap-4', `tw-grid-cols-3`]">
              <div class="tw-col-span-1">
                <base-currency-input label="Provider Price" :int-value.sync="row.providerPriceCents" v-model="row.providerPriceDisplay" :precision="0" :disabled="true" />
              </div>
              <div class="tw-col-span-1">
                <base-currency-input label="Price Floor" :int-value.sync="row.priceFloorCents" v-model="row.priceFloorDisplay" :precision="0" :disabled="true" />
              </div>

              <div class="tw-col-span-1">
                <base-currency-input label="Ceiling Price" :int-value.sync="row.ceilingPriceCents" v-model="row.ceilingPriceDisplay" :precision="0" :disabled="true" />
              </div>

              <div v-for="pgInput in row.programGroupInputs" :key="pgInput.businessProgramId" class="tw-col-span-1 mt-1">
                <base-currency-input
                  :label="formatProgramGroupName(pgInput.programGroup)"
                  :int-value.sync="pgInput.suggestedPriceCents"
                  v-model="pgInput.suggestedPriceDisplay"
                  :precision="0"
                  :disabled="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-between">
        <div v-if="uiRows.length > 0" class="tw-flex tw-gap-x-2">
          <base-button type="flat" @click="toggleSelectAll()" :disabled="isSaving">
            {{ allProgramsSelected ? "Deselect All" : "Select All" }}
          </base-button>
        </div>

        <div>
          <base-button type="flat" class="tw-w-auto tw-mr-3" :disabled="isSaving" @click="hide()">
            Cancel
          </base-button>
          <base-button :loading="isSaving" class="tw-w-auto" :disabled="!canCopy || isSaving" @click="copy()">
            Copy
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon } from "@/assets/svgs";
import { orderBy } from "lodash";
import { mapGetters } from "vuex"; // Keep mapGetters for accessing locations from Vuex

export default {
  name: "BusinessLocationCostMappingsSlideover",
  components: {
    ArrowRightIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingPrograms: false,
      isLoadingLocations: false,
      isSaving: false,
      selectedLocationIds: [],
      programsData: [],
      uiRows: [],
      inputValidationStatus: {},
      businessLocation: {},
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]),

    orderedLocations() {
      const excludeId = this.businessLocation.businessLocationId;
      return this.locations.filter((loc) => loc.businessLocationId !== excludeId).sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    },

    title() {
      if (this.selectedLocationIds.length === 0) {
        return "Copy Program Pricing";
      } else if (this.selectedLocationIds.length === 1) {
        const loc = this.locations.find((l) => l.businessLocationId === this.selectedLocationIds[0]);
        return loc ? `${loc.name} Program Pricing` : "Copy Program Pricing";
      } else {
        return `Copy Program Pricing for ${this.selectedLocationIds.length} Locations`;
      }
    },
    orderedUiRows() {
      return orderBy(this.uiRows, ["name"], ["asc"]);
    },
    canCopy() {
      // Must have at least one location selected
      if (this.selectedLocationIds.length === 0) {
        return false;
      }
      // Must have at least one program row selected
      const anyRowSelected = this.uiRows.some((row) => row.selected);
      if (!anyRowSelected) {
        return false;
      }

      // Check if all inputs of the *selected* programs are valid
      const selectedInputKeys = new Set();
      this.uiRows.forEach((row) => {
        if (row.selected) {
          // Add keys for the common price inputs for the row
          selectedInputKeys.add(`${row.name}_providerPrice`);
          selectedInputKeys.add(`${row.name}_priceFloor`);
          selectedInputKeys.add(`${row.name}_ceilingPrice`);
          // Add keys for individual program group inputs
          row.programGroupInputs.forEach((pgInput) => {
            selectedInputKeys.add(pgInput.businessProgramId);
          });
        }
      });

      // Ensure all collected validation statuses for selected inputs are true
      let allSelectedInputsAreValid = true;
      for (const key of selectedInputKeys) {
        // If a key exists in inputValidationStatus and its value is explicitly false, then it's invalid.
        // Undefined or true implies valid or no validation yet.
        if (this.inputValidationStatus[key] === false) {
          allSelectedInputsAreValid = false;
          break;
        }
      }
      return allSelectedInputsAreValid;
    },
    // Check if all program rows are selected
    allProgramsSelected() {
      if (this.uiRows.length === 0) return false;
      return this.uiRows.every((row) => row.selected);
    },
    anyProgramsSelected() {
      if (this.uiRows.length === 0) return false;
      return this.uiRows.some((row) => row.selected);
    },
  },
  methods: {
    formatCentsToDisplay(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents) || cents === null || cents === undefined) {
        return 0;
      }
      return (cents / 100).toFixed(2);
    },

    async show(businessLocationItem) {
      this.resetData();
      this.businessLocation = businessLocationItem;
      this.$refs.slideover.show();
      await this.fetchAndPrepareProgramData();
      this.$nextTick(() => {
        this.initializeInputValidationStatus();
      });
    },
    hide() {
      this.$refs.slideover.hide();
      this.resetData();
    },
    resetData() {
      this.isLoadingPrograms = false;
      this.isLoadingLocations = false;
      this.isSaving = false;
      this.selectedLocationIds = [];
      this.programsData = [];
      this.uiRows = [];
      this.inputValidationStatus = {};
      this.businessLocation = {};
    },
    async fetchAndPrepareProgramData() {
      try {
        await this.getPrograms();
        await this.getPricesMappings();
        this.applySavedPricesToProgramsData();
        this.buildUiRowsFromProgramsData();
      } catch (error) {
        console.error("Error fetching or preparing program data:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program data",
          message: "There was an issue fetching program pricing. Please try again.",
          error,
        });
      }
    },
    async getPrograms() {
      try {
        this.isLoadingPrograms = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);

        this.programsData = response.data.map((program) => ({
          ...program,
          suggestedPriceCents: 0,
          suggestedPriceDisplay: 0,
          providerPriceCents: 0,
          providerPriceDisplay: 0,
          priceFloorCents: 0,
          priceFloorDisplay: 0,
          ceilingPriceCents: 0,
          ceilingPriceDisplay: 0,
        }));
      } catch (error) {
        console.error("Error fetching master programs:", error);
        throw error;
      } finally {
        this.isLoadingPrograms = false;
      }
    },
    async getPricesMappings() {
      try {
        this.isLoadingMappings = true;
        const url = `/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings/program-prices`;
        const response = await this.$axios.get(url);

        this.programPricesMap = {};

        if (Array.isArray(response.data)) {
          response.data.forEach((mapping) => {
            const programId = mapping.businessProgramId;

            if (!programId) {
              return; // Skip this mapping if it doesn't have an ID
            }
            const suggestedPrice = mapping.suggestedPriceCents || 0;
            const providerPrice = mapping.providerPriceCents || 0;
            const priceFloor = mapping.priceFloorCents || 0; // Fetch new field
            const ceilingPrice = mapping.ceilingPriceCents || 0; // Fetch new field

            this.programPricesMap[programId] = {
              suggestedPriceCents: suggestedPrice,
              providerPriceCents: providerPrice,
              priceFloorCents: priceFloor, // Store new field
              ceilingPriceCents: ceilingPrice, // Store new field
            };
          });
        }
      } catch (error) {
        console.error("Error fetching program price mappings:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program price mappings",
          message: "There was an issue fetching program pricing. Please try again.",
          error,
        });
        throw error;
      } finally {
        this.isLoadingMappings = false;
      }
    },

    applySavedPricesToProgramsData() {
      this.programsData.forEach((program) => {
        const copydPrices = this.programPricesMap[program.businessProgramId];
        if (copydPrices) {
          program.suggestedPriceCents = copydPrices.suggestedPriceCents;
          program.suggestedPriceDisplay = this.formatCentsToDisplay(copydPrices.suggestedPriceCents);
          program.providerPriceCents = copydPrices.providerPriceCents;
          program.providerPriceDisplay = this.formatCentsToDisplay(copydPrices.providerPriceCents);
          program.priceFloorCents = copydPrices.priceFloorCents;
          program.priceFloorDisplay = this.formatCentsToDisplay(copydPrices.priceFloorCents);
          program.ceilingPriceCents = copydPrices.ceilingPriceCents;
          program.ceilingPriceDisplay = this.formatCentsToDisplay(copydPrices.ceilingPriceCents);
        }
      });
    },

    buildUiRowsFromProgramsData() {
      const tempUiRows = {};

      this.programsData.forEach((program) => {
        if (!program.programGroup) return;

        if (!tempUiRows[program.name]) {
          tempUiRows[program.name] = {
            name: program.name,
            providerPriceCents: program.providerPriceCents,
            providerPriceDisplay: program.providerPriceDisplay,
            priceFloorCents: program.priceFloorCents,
            priceFloorDisplay: program.priceFloorDisplay,
            ceilingPriceCents: program.ceilingPriceCents,
            ceilingPriceDisplay: program.ceilingPriceDisplay,
            programGroupInputs: [],
          };
        }

        tempUiRows[program.name].programGroupInputs.push(program);
      });

      this.uiRows = Object.values(tempUiRows)
        .filter((row) => row.programGroupInputs.length > 0)
        .map((row) => {
          row.selected = true;
          row.programGroupInputs = orderBy(row.programGroupInputs, ["programGroup"]);
          return row;
        });
    },

    initializeInputValidationStatus() {
      this.inputValidationStatus = {};
      this.uiRows.forEach((row) => {
        this.$set(this.inputValidationStatus, `${row.name}_providerPrice`, true);
        this.$set(this.inputValidationStatus, `${row.name}_priceFloor`, true);
        this.$set(this.inputValidationStatus, `${row.name}_ceilingPrice`, true);

        row.programGroupInputs.forEach((pgInput) => {
          this.$set(this.inputValidationStatus, pgInput.businessProgramId, true);
        });
      });
    },
    toggleSelectAll() {
      const targetSelection = !this.allProgramsSelected;
      this.uiRows.forEach((row) => {
        this.$set(row, "selected", targetSelection);
        row.programGroupInputs.forEach((pg) => {
          const program = this.programsData.find((p) => p.businessProgramId === pg.businessProgramId);
          if (program) {
            program.selected = targetSelection;
          }
        });
      });
    },

    syncPricesToProgramsData() {
      const updatedProgramsDataMap = new Map(this.programsData.map((p) => [p.businessProgramId, p]));

      this.uiRows.forEach((uiRow) => {
        const sharedProviderPrice = uiRow.providerPriceCents;
        const sharedPriceFloor = uiRow.priceFloorCents;
        const sharedCeilingPrice = uiRow.ceilingPriceCents;

        uiRow.programGroupInputs.forEach((pgInput) => {
          const programInProgramsData = updatedProgramsDataMap.get(pgInput.businessProgramId);

          if (programInProgramsData) {
            programInProgramsData.providerPriceCents = sharedProviderPrice;
            programInProgramsData.priceFloorCents = sharedPriceFloor;
            programInProgramsData.ceilingPriceCents = sharedCeilingPrice;
            programInProgramsData.suggestedPriceCents = pgInput.suggestedPriceCents;
            programInProgramsData.selected = uiRow.selected;
          }
        });
      });

      this.programsData = Array.from(updatedProgramsDataMap.values());
    },

    formatProgramGroupName(groupName) {
      if (!groupName) {
        return "";
      }
      const match = groupName.match(/^(\d+\s*-\s*|)(\s*)(.*)$/);
      if (match && match[3]) {
        return match[3];
      }
      return groupName;
    },

    async copy() {
      if (this.selectedLocationIds.length === 0) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "No Locations Selected",
          message: "Please select at least one location to copy program pricing.",
        });
        return;
      }
      if (!this.canCopy) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Validation Error",
          message: "Please correct all input errors or select at least one program before saving.",
        });
        return;
      }

      this.isSaving = true;
      const programsToUpdate = [];

      this.syncPricesToProgramsData(); // Ensure programsData is up-to-date with UI

      // Filter programsData to include only programs from selected UI rows
      this.programsData.forEach((programDetail) => {
        if (programDetail.selected) {
          programsToUpdate.push({
            businessProgramId: programDetail.businessProgramId,
            suggestedPriceCents: Math.round(programDetail.suggestedPriceCents) * 100,
            providerPriceCents: Math.round(programDetail.providerPriceCents) * 100,
            priceFloorCents: Math.round(programDetail.priceFloorCents) * 100,
            ceilingPriceCents: Math.round(programDetail.ceilingPriceCents) * 100,
          });
        }
      });

      if (programsToUpdate.length === 0) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "No Programs Selected",
          message: "Please select at least one program to copy its pricing.",
        });
        this.isSaving = false;
        return;
      }
      const locationIdsToSend = this.selectedLocationIds.map((location) => {
        return typeof location === "object" ? location.businessLocationId : location;
      });

      try {
        await this.$axios.patch(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings/program-prices`, {
          locationIds: locationIdsToSend,
          programs: programsToUpdate,
          isApplyToAllLocations: false,
        });

        this.hide();
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Program Prices Updated",
          message: `Program pricing copied successfully.`,
        });
      } catch (error) {
        console.error("Error saving program prices:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to copy program prices",
          message: `There was an issue saving the program prices. Please try again.`,
          error,
        });
        throw error;
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
